import {
    ComparisonObject,
    ComparisonOverlayRegion,
} from '~/engine/data/common/customs/ComparisonObject';
import { Environment } from '~/engine/data/Environment';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Attach one {@link ComparisonObject} overlay per plane view and repaint the
 * contour layer, returning a teardown that removes the overlays and repaints
 * again.
 *
 * The first view reuses `regions`; every other view gets its OWN clone of each
 * region texture, because a view delete()s whatever is removed from it (see
 * {@link TextureView.removeCustomObject}) — sharing one instance would let a
 * single view's teardown free the textures the other views still draw.
 *
 * Shared by the contour-comparison overlay ({@link useContourComparison}) and
 * the reference-diff overlay ({@link useReferenceDiff}): each caller builds its
 * own coloured `regions`, and the overlays then own and free those textures.
 */
export function attachComparisonOverlays(
    views: readonly PlaneView[],
    regions: ComparisonOverlayRegion[],
    environment: Environment
): () => void {
    const overlays = views.map((view, index) => {
        const overlay = new ComparisonObject();
        overlay.setRegions(
            index === 0
                ? regions
                : regions.map((region) => ({
                      ...region,
                      texture: region.texture.clone(),
                  }))
        );
        view.addCustomObject(overlay);
        return overlay;
    });
    environment.triggerContourLayerUpdate();

    return () => {
        views.forEach((view, index) => {
            view.removeCustomObject(overlays[index]);
        });
        environment.triggerContourLayerUpdate();
    };
}
